import { useEffect, useRef, useState } from "react";
import { HTML_PREVIEW_ORIGIN, isIsolatedPreviewOrigin } from "../lib/filePreview";

const READY_TIMEOUT_MS = 6000;

interface Props {
  /** Raw agent HTML, rendered with scripts on the isolated preview origin. */
  html: string;
  /** Sanitised, script-free document used when the preview origin is unavailable. */
  fallbackDocument: string;
  title: string;
  labels: { live: string; fallback: string };
  previewOrigin?: string;
}

/**
 * Shows agent HTML with its scripts (charts, CDN styles) working, without ever running it on our origin:
 * the page is handed to web/html-preview on a separate origin, which renders it in an allow-scripts-only
 * sandbox under a no-network CSP. If that origin does not answer, we fall back to the static, sanitised render.
 */
export function HtmlPreviewFrame({ html, fallbackDocument, title, labels, previewOrigin = HTML_PREVIEW_ORIGIN }: Props) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const isolated = isIsolatedPreviewOrigin(previewOrigin, window.location.origin);
  const [mode, setMode] = useState<"live" | "fallback">(isolated ? "live" : "fallback");

  useEffect(() => {
    if (!isolated) return;
    setMode("live");
    let ready = false;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== previewOrigin || event.source !== frameRef.current?.contentWindow) return;
      if ((event.data as { type?: string } | null)?.type !== "remote-preview-ready") return;
      ready = true;
      frameRef.current?.contentWindow?.postMessage({ type: "remote-preview-render", html, title }, previewOrigin);
    };
    window.addEventListener("message", onMessage);
    const timer = window.setTimeout(() => {
      if (!ready) setMode("fallback");
    }, READY_TIMEOUT_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
    };
  }, [html, isolated, previewOrigin, title]);

  if (mode === "fallback") {
    return (
      <div className="file-preview-html">
        <p>{labels.fallback}</p>
        <iframe title={title} sandbox="" referrerPolicy="no-referrer" srcDoc={fallbackDocument} tabIndex={-1} />
      </div>
    );
  }
  return (
    <div className="file-preview-html">
      <p>{labels.live}</p>
      <iframe
        ref={frameRef}
        title={title}
        src={`${previewOrigin}/`}
        // Cross-origin, so allow-same-origin only lets the renderer keep *its own* origin (needed for
        // postMessage targeting). It never grants access to ours; the agent HTML runs one level deeper.
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer"
        allow=""
        tabIndex={-1}
      />
    </div>
  );
}
